import React from 'react';
import { FEATURE_FLAGS } from '../../../engine/enhancers/questionEnhancer';

const AIPerformanceIndicator = ({ 
  performance, 
  targetDifficulty, 
  totalQuestions, 
  answeredQuestions,
  className = "" 
}) => {
  if (!FEATURE_FLAGS.AI_SELECTION) return null;

  const performancePercent = Math.round(performance * 100);
  const completionPercent = Math.round((answeredQuestions / Math.max(totalQuestions, 1)) * 100);

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getPerformanceColor = (perf) => {
    if (perf >= 70) return 'text-green-600';
    if (perf >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium text-gray-900 flex items-center">
          <span className="mr-2">🤖</span>
          AI Performance Tracker
        </h4>
        <div className="text-xs text-gray-500">
          {answeredQuestions}/{totalQuestions} questions
        </div>
      </div>

      <div className="space-y-3">
        {/* Performance Score */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Performance</span>
          <div className="flex items-center space-x-2">
            <div className="w-16 bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full ${
                  performancePercent >= 70 ? 'bg-green-500' :
                  performancePercent >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${performancePercent}%` }}
              />
            </div>
            <span className={`text-sm font-medium ${getPerformanceColor(performancePercent)}`}>
              {performancePercent}%
            </span>
          </div>
        </div>

        {/* Target Difficulty */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Next Difficulty</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(targetDifficulty || 'medium')}`}>
            {(targetDifficulty || 'medium').toUpperCase()}
          </span>
        </div>

        {/* Completion Progress */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Progress</span>
          <div className="flex items-center space-x-2">
            <div className="w-16 bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
            <span className="text-sm font-medium text-indigo-600">
              {completionPercent}%
            </span>
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="mt-3 pt-3 border-t">
        <div className="text-xs text-gray-500">
          {performance < 0.3 && "📉 Adjusting to easier questions"}
          {performance >= 0.3 && performance <= 0.7 && "⚖️ Maintaining balanced difficulty"}
          {performance > 0.7 && "📈 Increasing challenge level"}
        </div>
      </div>
    </div>
  );
};

export default AIPerformanceIndicator;